<script setup lang="ts">
import {computed} from "vue";
import StatusDot from "@/core/components/ui/badge/StatusDot.vue";
import {statusDotEmojis, StatusDotState} from "@/core/components/ui/badge/statusDot";

/**
 * A text from the API rendered with a discreet colored dot wherever it holds
 * a state marker emoji (🟢 🟠 🔴 ⚪), the rest of the text untouched.
 *
 * Each dot is glued to the words it qualifies, up to the next " · " or line
 * break, so a wrap never separates a dot from its pet or family name.
 */
const props = defineProps<{
    text: string;
}>();

interface Segment {
    text: string;
    state?: StatusDotState;
}

const splitter = new RegExp(`(${Object.keys(statusDotEmojis).join('|')})`, 'u');
const separator = /( · |\n)/;

const segments = computed<Segment[]>(() => {
    const parts = props.text.split(splitter);
    const result: Segment[] = [];

    parts.forEach((part, index) => {
        if (part === '') {
            return;
        }

        const state = statusDotEmojis[part];

        if (!state) {
            result.push({text: part});

            return;
        }

        const next = parts[index + 1] && !statusDotEmojis[parts[index + 1]] ? parts[index + 1] : '';
        const boundary = next.search(separator);
        const glued = boundary === -1 ? next : next.slice(0, boundary);

        result.push({text: glued, state});
        parts[index + 1] = next.slice(glued.length);
    });

    return result;
});
</script>

<template>
  <span>
    <template v-for="(segment, index) in segments" :key="index">
      <span v-if="segment.state" class="whitespace-nowrap"><StatusDot :state="segment.state" class="mx-0.5 -mt-0.5" />{{ segment.text }}</span><template v-else>{{ segment.text }}</template>
    </template>
  </span>
</template>
